import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, Loader2, CheckCircle, Info } from 'lucide-react';
import axios from 'axios';

export default function StripeCheckoutPage({ token, setUser }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const tier = searchParams.get('tier') || 'pro';
  const billing = searchParams.get('billing') || 'monthly';
  const sessionParam = searchParams.get('session_id');

  const [sessionId, setSessionId] = useState(sessionParam || '');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Stripe form fields state
  const [email, setEmail] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardName, setCardName] = useState('');

  const plans = {
    starter: { name: 'Identity Starter', monthly: 4.99, annual: 39.99 },
    pro: { name: 'Align Pro', monthly: 9.99, annual: 79.99 },
    elite: { name: 'Elite Pioneer', monthly: 24.99, annual: 199.99 },
  };

  const selectedPlan = plans[tier] || plans.pro;
  const price = billing === 'monthly' ? selectedPlan.monthly : selectedPlan.annual;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!sessionId) {
      // Create session id on server
      axios.post(`${BACKEND_URL}/api/payment/stripe-session`, 
        { tier, billing_period: billing },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(res => {
        setSessionId(res.data.session_id);
      })
      .catch(err => {
        setError('Failed to initialize Stripe checkout session.');
      });
    }
  }, [sessionId, tier, billing, token, BACKEND_URL]);

  const handlePay = async (simulateSuccess = true) => {
    if (!email || !cardNumber || !cardExpiry || !cardCvc || !cardName) {
      setError('Please fill in all card details.');
      return;
    }

    setLoading(true);
    setError('');

    // Simulate network latency
    await new Promise(r => setTimeout(r, 1800));

    if (!simulateSuccess) {
      setError('Your card was declined. Please try again or use another payment method.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/payment/stripe-verify`,
        { session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUser(response.data);
      setSuccess(true);

      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Stripe payment verification failed.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex flex-col items-center justify-center p-6 text-center">
        <CheckCircle className="h-16 w-16 text-emerald-500 mb-4 animate-bounce" />
        <h2 className="text-2xl font-bold text-neutral-800 mb-2">Payment Completed Successfully</h2>
        <p className="text-sm text-neutral-500 mb-4">Redirecting you to Align Dashboard...</p>
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-neutral-800 flex flex-col md:flex-row antialiased font-sans">
      
      {/* Left Column: Product Summary */}
      <div className="w-full md:w-[45%] bg-[#F8F9FA] border-b md:border-b-0 md:border-r border-neutral-200 p-8 md:p-16 flex flex-col justify-between">
        <div>
          <button
            onClick={() => navigate(`/checkout/payment-method?tier=${tier}&billing=${billing}`)}
            className="flex items-center gap-2 text-neutral-500 hover:text-neutral-800 text-sm font-medium mb-12 transition"
          >
            <ArrowLeft className="h-4 w-4" /> Back to checkout selection
          </button>

          <div className="space-y-4">
            <span className="text-sm font-semibold tracking-wider text-neutral-500 uppercase">Subscribe to Align OS</span>
            <h1 className="text-3xl font-bold text-neutral-900">{selectedPlan.name}</h1>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-extrabold text-neutral-900">${price}</span>
              <span className="text-neutral-500 font-medium text-sm">/{billing === 'monthly' ? 'month' : 'year'}</span>
            </div>

            <div className="border-t border-neutral-200 pt-6 mt-6 space-y-3">
              <div className="flex justify-between text-sm text-neutral-600">
                <span>{selectedPlan.name} × 1</span>
                <span>${price}</span>
              </div>
              <div className="flex justify-between text-sm text-neutral-400 font-light">
                <span>Taxes</span>
                <span>$0.00</span>
              </div>
              <div className="border-t border-neutral-200 pt-4 flex justify-between font-semibold text-neutral-900 text-lg">
                <span>Total due today</span>
                <span>${price}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 flex items-center gap-1.5 text-xs text-neutral-400 font-light">
          <Lock className="h-3.5 w-3.5 text-neutral-400" />
          <span>Powered by Stripe (Mock Sandbox)</span>
        </div>
      </div>

      {/* Right Column: Checkout Form */}
      <div className="w-full md:w-[55%] p-8 md:p-16 flex flex-col justify-center max-w-xl mx-auto">
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-neutral-900">Pay with Card</h2>

          {error && (
            <div className="p-4 bg-red-50 text-red-800 text-sm rounded-lg border border-red-200 flex gap-3">
              <Info className="h-5 w-5 text-red-600 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-500 uppercase mb-1.5">Email Address</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#635BFF]/30 focus:border-[#635BFF] transition text-sm text-neutral-900 shadow-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-500 uppercase mb-1.5">Card Information</label>
              <div className="rounded-lg border border-neutral-300 overflow-hidden shadow-sm focus-within:ring-2 focus-within:ring-[#635BFF]/30 focus-within:border-[#635BFF]">
                <input
                  type="text"
                  placeholder="Card Number"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  className="w-full px-4 py-2.5 border-b border-neutral-200 focus:outline-none text-sm text-neutral-900"
                />
                <div className="flex">
                  <input
                    type="text"
                    placeholder="MM/YY"
                    value={cardExpiry}
                    onChange={(e) => setCardExpiry(e.target.value)}
                    className="w-1/2 px-4 py-2.5 border-r border-neutral-200 focus:outline-none text-sm text-neutral-900"
                  />
                  <input
                    type="text"
                    placeholder="CVC"
                    value={cardCvc}
                    onChange={(e) => setCardCvc(e.target.value)}
                    className="w-1/2 px-4 py-2.5 focus:outline-none text-sm text-neutral-900"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-500 uppercase mb-1.5">Cardholder Name</label>
              <input
                type="text"
                placeholder="Full name on card"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#635BFF]/30 focus:border-[#635BFF] transition text-sm text-neutral-900 shadow-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-500 uppercase mb-1.5">Country or Region</label>
              <select className="w-full px-4 py-2.5 rounded-lg border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#635BFF]/30 focus:border-[#635BFF] transition text-sm text-neutral-900 shadow-sm">
                <option>United States</option>
                <option>India</option>
                <option>Canada</option>
                <option>United Kingdom</option>
              </select>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-3 pt-4">
            <button
              onClick={() => handlePay(true)}
              disabled={loading || !sessionId}
              className="w-full py-3 px-4 rounded-lg font-semibold bg-[#635BFF] hover:bg-[#5851EA] text-white shadow-md transition active:scale-[0.99] flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Processing Payment...</span>
                </>
              ) : (
                <span>Subscribe</span>
              )}
            </button>

            <button
              onClick={() => handlePay(false)}
              disabled={loading || !sessionId}
              className="w-full py-3 px-4 rounded-lg font-semibold border border-red-200 text-red-600 hover:bg-red-50 transition active:scale-[0.99] disabled:opacity-50 text-sm"
            >
              Simulate Card Declined
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}
